<template>
    <header class="top-navbar">
        <div class="top-navbar-left">
            <button
                type="button"
                class="sidebar-toggle-btn"
                @click="handleToggleSidebar"
                :aria-expanded="sidebarExpanded.toString()"
            >
                <font-awesome-icon :icon="['fas', 'bars']" />
            </button>
            <div class="navbar-logo" style="display: none">
                <BrandLogo alt="Bid Match" />
            </div>
            <div class="quick-actions" @click.stop>
                <button
                    type="button"
                    class="quick-actions-toggle"
                    aria-label="Quick actions"
                    @click="toggleQuickActions"
                    :aria-expanded="quickActionsOpen.toString()"
                >
                    <font-awesome-icon :icon="['fas', 'bolt']" />
                    <span class="ms-2">Quick Actions</span>
                    <font-awesome-icon
                        class="ms-1"
                        :icon="['fas', quickActionsOpen ? 'angle-up' : 'angle-down']"
                    />
                </button>
                <transition name="fade">
                    <ul v-if="quickActionsOpen" class="quick-actions-menu">
                        <li v-for="action in visibleQuickActions" :key="action.action">
                            <button type="button" @click="handleQuickAction(action.action)">
                                <font-awesome-icon :icon="['fas', action.icon]" />
                                <span class="ms-2">{{ action.title }}</span>
                            </button>
                        </li>
                    </ul>
                </transition>
            </div>
            <div class="topbar-status" v-if="process || pnCountDisplay">
                <div class="pulse">
                    <span v-if="process" class="pulse_item">{{ process }}</span>
                    <span v-if="pnCountDisplay" class="pulse_item">{{ pnCountDisplay }}</span>
                    <span v-if="subCountDisplay" class="pulse_item">{{ subCountDisplay }}</span>
                </div>
            </div>
        </div>
        <div class="top-navbar-right">
            <button
                type="button"
                class="theme-toggle-btn"
                :aria-label="themeMode === 'dark' ? 'Light Mode' : 'Dark Mode'"
                :aria-pressed="(themeMode === 'dark').toString()"
                @click="$emit('toggleTheme')"
            >
                <font-awesome-icon
                    :icon="['fas', themeMode === 'dark' ? 'sun' : 'moon']"
                />
                <span class="ms-2">{{ themeMode === 'dark' ? 'Light Mode' : 'Dark Mode' }}</span>
            </button>
            <button type="button" class="help-btn" aria-label="Shortcuts" @click="openShortcutsHelp">
                <font-awesome-icon :icon="['fas', 'keyboard']" />
                <span class="ms-2">Shortcuts</span>
            </button>
            <!-- <div class="searchInputWrapper">
                <input
                    class="searchInput"
                    type="text"
                    placeholder="Search"
                    v-model="searchTerm"
                    @keyup.enter="emitSearch"
                />
                <button type="button" class="searchInputIcon" @click="emitSearch">
                    <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                </button>
            </div> -->
            <div
                class="user-menu"
                @click.stop="toggleUserMenu"
                :style="loading ? 'pointer-events: none; opacity: 0.6;' : ''"
            >
                <font-awesome-icon
                    v-if="sessionWarning"
                    class="session-warning"
                    :icon="['fas', 'circle-exclamation']"
                    :title="sessionWarning"
                />
                <div class="user-initials">
                    <span>{{ userInitials }}</span>
                    <font-awesome-icon
                        class="user-chevron"
                        :icon="['fas', userMenuOpen ? 'chevron-up' : 'chevron-down']"
                    />
                </div>
                <transition name="fade">
                    <ul v-if="userMenuOpen" class="user-dropdown">
                        <li v-if="showProfileConfig">
                            <button type="button" @click="goToProfile" :disabled="loading">
                                <font-awesome-icon :icon="['fas', 'user']" />
                                <span class="ms-2">Profile config</span>
                            </button>
                        </li>
                        <li>
                            <button type="button" @click="doLogout" :disabled="loading">
                                <font-awesome-icon :icon="['fas', 'right-from-bracket']" />
                                <span class="ms-2">Logout</span>
                            </button>
                        </li>
                    </ul>
                </transition>
            </div>
        </div>
    </header>
    <transition name="fade">
        <div v-if="showShortcutsHelp" class="shortcuts-modal-backdrop" @click="closeShortcutsHelp">
            <div class="shortcuts-modal" @click.stop>
                <div class="shortcuts-modal__header">
                    <h5 class="shortcuts-modal__title">{{ shortcutHelpTitle }}</h5>
                    <button type="button" class="shortcuts-modal__close" @click="closeShortcutsHelp">
                        <span aria-hidden="true">&times;</span>
                        <span class="visually-hidden">Close</span>
                    </button>
                </div>
                <div class="shortcuts-modal__body">
                    <table class="shortcuts-table">
                        <thead>
                            <tr>
                                <th>Keys</th>
                                <th>Action</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in shortcutHelpItems" :key="`${item.combo}-${item.action}`">
                                <td><code>{{ formatShortcutLabel(item.combo) }}</code></td>
                                <td>{{ item.action }}</td>
                                <td>{{ item.description }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </transition>
    <div v-if="loading" class="logout-spinner-overlay">
        <div class="logout-modal">
            <div class="logout-modal-bar"></div>
            <BrandLogo alt="Greenwood Aerospace" class="logout-logo mb-3" />
            <div class="spinner-border logout-spinner-color mb-3" role="status">
                <span class="sr-only">Loading...</span>
            </div>
            <div class="logout-text">Signing Out</div>
        </div>
    </div>
</template>

<script>
    import { mapStores } from 'pinia'
    import { useSessionStore } from '@/stores/sessionStore'
    import { useUserStore } from '@/stores/userStore'
    import config from '@/services/config'
    import BrandLogo from '@/components/shared/BrandLogo.vue'
    import { createKeyboardShortcuts } from '@/utils/keyboardShortcuts'

    export default {
        name: 'TopNavbar',
        components: { BrandLogo },
        emits: [
            'toggleSidebar',
            'resetSearch',
            'copyParts',
            'showParts',
            'editParts',
            'editCustomerRef',
            'fuzzyPartsMatches',
            'downloadQuote',
            'search',
            'toggleTheme',
        ],
        props: {
            enableGlobalSearch: {
                type: Boolean,
                default: true,
            },
            themeMode: {
                type: String,
                default: 'light',
            },
            shortcutHelpTitle: {
                type: String,
                default: 'Page Shortcuts',
            },
            shortcutHelpItems: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                backendURL: process.env.VUE_APP_BACKEND_URL,
                quickActionsOpen: false,
                userMenuOpen: false,
                showShortcutsHelp: false,
                searchTerm: '',
                sidebarExpanded: false,
                loading: false,
                shortcutHelpManager: null,
                quickActions: [
                    { title: 'Copy Parts', action: 'copy_parts', icon: 'copy' },
                    { title: 'Show Parts', action: 'show_parts', icon: 'eye' },
                    { title: 'Edit Parts', action: 'edit_parts', icon: 'edit' },
                    { title: 'Edit Customer/Ref', action: 'edit_customer_ref', icon: 'id-card' },
                    { title: 'Reset Search', action: 'reset_search', icon: 'rotate-left' },
                    {
                        title: 'Fuzzy Part Match',
                        action: 'fuzzy_part_matches',
                        icon: 'wand-magic-sparkles',
                    },
                    { title: 'Download Quote', action: 'download_quote', icon: 'download' },
                ],
            }
        },
        computed: {
            ...mapStores(useSessionStore, useUserStore),
            hasUserProfile() {
                return (
                    Boolean(this.userStore.getFirstName) ||
                    Boolean(this.userStore.getLastName) ||
                    (this.userStore.getUsername && this.userStore.getUsername !== 'NONE')
                )
            },
            userInitials() {
                const firstInitial = this.userStore.getFirstName
                    ? this.userStore.getFirstName.charAt(0)
                    : ''
                const lastInitial = this.userStore.getLastName
                    ? this.userStore.getLastName.charAt(0)
                    : ''

                if (firstInitial || lastInitial) {
                    return `${firstInitial}${lastInitial}`.toUpperCase()
                }

                const usernameInitial = this.userStore.getUsername
                    ? this.userStore.getUsername.charAt(0)
                    : 'U'
                return usernameInitial.toUpperCase()
            },
            sessionWarning() {
                if (this.sessionStore.isSessionExpiringHour) {
                    return 'Session expiring soon. Logout to renew.'
                }
                if (this.sessionStore.isSessionExpiringToday) {
                    return 'Session expiring today. Logout to renew.'
                }
                return ''
            },
            pnCountDisplay() {
                const c = this.searchStore.pulse_pn_count
                const t = this.searchStore.pulse_pn_total
                if (c != null && t != null) return `${c}/${t}`
                return ''
            },
            subCountDisplay() {
                const c = this.searchStore.pulse_sub_count
                const t = this.searchStore.pulse_sub_total
                if (c != null && t != null) return `(sub ${c}/${t})`
                return ''
            },
            process() {
                return this.searchStore.pulse_process
            },
            showProfileConfig() {
                return !config.isSearchDemo?.()
            },
            visibleQuickActions() {
                if (!config.isSearchDemo?.()) {
                    return this.quickActions
                }
                return this.quickActions.filter(
                    (action) =>
                        !['edit_customer_ref', 'download_quote'].includes(action.action)
                )
            },
        },
        mounted() {
            document.addEventListener('click', this.handleOutsideClick)
            this.shortcutHelpManager = createKeyboardShortcuts({
                target: window,
                capture: true,
                blockCtrlMeta: true,
                bindings: [
                    {
                        combo: 'alt+shift+k',
                        action: () => this.toggleShortcutsHelp(),
                    },
                ],
            })
            this.shortcutHelpManager.start()
        },
        beforeUnmount() {
            document.removeEventListener('click', this.handleOutsideClick)
            this.shortcutHelpManager?.stop?.()
        },
        methods: {
            toggleQuickActions() {
                this.quickActionsOpen = !this.quickActionsOpen
            },
            toggleUserMenu() {
                this.userMenuOpen = !this.userMenuOpen
            },
            handleToggleSidebar() {
                this.sidebarExpanded = !this.sidebarExpanded
                this.$emit('toggleSidebar')
            },
            handleOutsideClick(event) {
                if (!this.$el.contains(event.target)) {
                    this.quickActionsOpen = false
                    this.userMenuOpen = false
                }
            },
            openShortcutsHelp() {
                this.showShortcutsHelp = true
                this.quickActionsOpen = false
                this.userMenuOpen = false
            },
            toggleShortcutsHelp() {
                if (this.showShortcutsHelp) {
                    this.closeShortcutsHelp()
                    return
                }
                this.openShortcutsHelp()
            },
            closeShortcutsHelp() {
                this.showShortcutsHelp = false
            },
            formatShortcutLabel(combo) {
                if (!combo) return ''
                return combo
                    .split('+')
                    .map((token) => token.trim())
                    .filter(Boolean)
                    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
                    .join('+')
            },
            handleQuickAction(action) {
                if (action === 'reset_search') {
                    this.$emit('resetSearch')
                } else if (action === 'copy_parts') {
                    this.$emit('copyParts')
                } else if (action === 'show_parts') {
                    this.$emit('showParts')
                } else if (action === 'edit_parts') {
                    this.$emit('editParts')
                } else if (action === 'fuzzy_part_matches') {
                    this.$emit('fuzzyPartsMatches')
                } else if (action === 'edit_customer_ref') {
                    this.$emit('editCustomerRef')
                } else if (action === 'download_quote') {
                    this.$emit('downloadQuote')
                }

                this.quickActionsOpen = false
            },
            emitSearch() {
                const term = this.searchTerm.trim()
                if (!term) {
                    return
                }

                this.$emit('search', term)

                if (this.enableGlobalSearch) {
                    this.navigateToDashboardSearch(term)
                }
            },
            navigateToDashboardSearch(term) {
                const normalizedTerm = term.toUpperCase()
                this.$router
                    .push({
                        name: 'dashboard',
                        query: {
                            part: normalizedTerm,
                        },
                    })
                    .catch((error) => {
                        if (error?.name !== 'NavigationDuplicated') {
                            console.error('Failed to navigate to dashboard search', error)
                        }
                    })
            },
            goToProfile() {
                this.$router.push({ name: 'profile-config' })
                this.userMenuOpen = false
            },
            async doLogout() {
                this.loading = true
                try {
                    await this.userStore.doLogout()
                } catch (error) {
                    console.error('Logout failed:', error)
                    this.loading = false
                }
            },
        },
    }
</script>

<style scoped lang="scss">
    .top-navbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: var(--color-surface-inverse);
        padding: 0.75rem 1.5rem;
        /*box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);*/
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1040;
        margin-bottom: 1.5rem;
        gap: 1rem;
    }

    .top-navbar-left,
    .top-navbar-right {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .quick-actions {
        position: relative;
    }

    .sidebar-toggle-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        background: var(--color-surface-canvas);
        box-shadow: var(--shadow-md);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: transform 0.2s ease;
    }

    .sidebar-toggle-btn:hover {
        transform: scale(1.05);
    }

    .navbar-logo img {
        height: 32px;
        width: auto;
        display: block;
    }

    .quick-actions-toggle {
        display: flex;
        align-items: center;
        padding: 0.5rem 0.9rem;
        border-radius: var(--radius-pill);
        border: none;
        background: var(--color-accent-soft);
        color: var(--color-text-primary);
        font-weight: 600;
        cursor: pointer;
        transition: transform 0.2s ease;
    }

    .quick-actions-toggle:hover {
        transform: translateY(-1px);
    }

    .quick-actions-menu {
        position: absolute;
        top: calc(100% + 0.5rem);
        left: 0;
        min-width: 220px;
        background: var(--color-surface-canvas);
        border-radius: var(--radius-lg);
        padding: 0.5rem;
        box-shadow: var(--shadow-lg);
        list-style: none;
        margin: 0;
        z-index: 1040;
    }

    .quick-actions-menu li + li {
        margin-top: 0.25rem;
    }

    .quick-actions-menu button {
        width: 100%;
        display: flex;
        align-items: center;
        padding: 0.5rem 0.75rem;
        border-radius: 12px;
        border: none;
        background: transparent;
        font-size: 0.9rem;
        color: var(--color-text-primary);
        transition: background 0.2s ease;
        cursor: pointer;
    }

    .quick-actions-menu button:hover {
        background: var(--color-accent-soft);
    }

    .topbar-status {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--color-text-inverse);
        font-size: 0.9rem;
    }

    .searchInputWrapper {
        position: relative;
        display: flex;
        align-items: center;
    }

    .searchInput {
        padding: 0.5rem 2.5rem 0.5rem 0.9rem;
        border-radius: var(--radius-pill);
        border: 1px solid var(--color-border-accent);
        outline: none;
        min-width: 220px;
        transition: border 0.2s ease;
        background-color: var(--color-surface-canvas);
        color: var(--color-text-primary);
    }

    .searchInput:focus {
        border-color: var(--color-border-accent-strong);
        box-shadow: var(--shadow-focus-brand);
    }

    .searchInputIcon {
        position: absolute;
        right: 0.6rem;
        background: transparent;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: var(--color-brand-primary);
    }

    .help-btn {
        display: inline-flex;
        align-items: center;
        border: 1px solid var(--color-border-accent);
        border-radius: var(--radius-pill);
        background: var(--color-surface-inverse);
        color: var(--color-text-inverse);
        font-size: 0.9rem;
        font-weight: 600;
        padding: 0.5rem 0.95rem;
        margin-right: 0.5rem;
        transition: all 0.15s ease;
    }

    .theme-toggle-btn {
        display: inline-flex;
        align-items: center;
        border: 1px solid var(--color-border-accent);
        border-radius: var(--radius-pill);
        background: var(--color-surface-canvas);
        color: var(--color-text-primary);
        font-size: 0.9rem;
        font-weight: 600;
        padding: 0.5rem 0.95rem;
        transition: all 0.15s ease;
    }

    .theme-toggle-btn:hover {
        border-color: var(--color-brand-primary);
        color: var(--color-brand-primary);
        transform: translateY(-1px);
    }

    .help-btn:hover {
        background: var(--color-brand-primary);
        border-color: var(--color-brand-primary);
        color: var(--color-text-inverse);
    }

    .user-menu {
        position: relative;
        display: flex;
        align-items: center;
        cursor: pointer;
    }

    .session-warning {
        color: var(--color-text-warning);
        margin-right: 0.5rem;
        font-size: 1.1rem;
    }

    .user-initials {
        min-width: 40px;
        height: 40px;
        border-radius: var(--radius-pill);
        background: var(--color-surface-inverse);
        color: var(--color-text-inverse);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.3rem;
        padding: 0 0.85rem;
        font-weight: 600;
        text-transform: uppercase;
        margin-left: 25px;
    }

    .user-initials span {
        line-height: 1;
    }

    .user-chevron {
        font-size: 0.75rem;
    }

    .user-dropdown {
        position: absolute;
        top: calc(100% + 0.5rem);
        right: 0;
        min-width: 180px;
        background: var(--color-surface-canvas);
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-lg);
        list-style: none;
        padding: 0.5rem;
        margin: 0;
        z-index: 1040;
    }

    .user-dropdown li + li {
        margin-top: 0.25rem;
    }

    .user-dropdown button {
        width: 100%;
        display: flex;
        align-items: center;
        padding: 0.5rem 0.75rem;
        border: none;
        border-radius: 12px;
        background: transparent;
        font-size: 0.9rem;
        color: var(--color-text-primary);
        cursor: pointer;
        transition: background 0.2s ease;
    }

    .user-dropdown button:hover {
        background: var(--color-accent-soft);
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.15s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }

    .logout-spinner-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: var(--color-surface-overlay-soft);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: all;
    }

    .logout-spinner-color {
        color: var(--color-brand-primary);
    }

    .shortcuts-modal-backdrop {
        position: fixed;
        inset: 0;
        z-index: var(--shell-modal-z);
        background: var(--color-surface-overlay);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
    }

    .shortcuts-modal {
        width: min(920px, 100%);
        max-height: 80vh;
        overflow: auto;
        background: var(--color-surface-canvas);
        border-radius: var(--radius-xl);
        border: 1px solid var(--color-border-default);
        box-shadow: var(--shadow-xl);
    }

    .shortcuts-modal__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid var(--color-border-soft);
        padding: 0.95rem 1rem;
        background: linear-gradient(90deg, var(--color-surface-inverse) 0%, #2a2a2a 100%);
        border-top-left-radius: var(--radius-xl);
        border-top-right-radius: var(--radius-xl);
    }

    .shortcuts-modal__title {
        margin: 0;
        font-size: 1.05rem;
        font-weight: 700;
        color: var(--color-text-inverse);
        letter-spacing: 0.01em;
    }

    .shortcuts-modal__close {
        border: none;
        background: transparent;
        font-size: 1.5rem;
        line-height: 1;
        color: var(--color-text-inverse);
        opacity: 0.85;
    }

    .shortcuts-modal__close:hover {
        opacity: 1;
    }

    .shortcuts-modal__body {
        padding: 0.95rem 1rem 1rem;
    }

    .shortcuts-table {
        width: 100%;
        border-collapse: collapse;
        border: 1px solid var(--color-border-muted);
        border-radius: var(--radius-md);
        overflow: hidden;
    }

    .shortcuts-table th,
    .shortcuts-table td {
        padding: 0.68rem 0.75rem;
        border-bottom: 1px solid var(--color-border-soft);
        text-align: left;
        vertical-align: top;
        color: var(--color-text-primary);
        font-size: 0.9rem;
    }

    .shortcuts-table th {
        background: var(--color-surface-inverse);
        color: var(--color-text-inverse);
        font-weight: 700;
        border-bottom: 1px solid var(--color-border-strong);
    }

    .shortcuts-table tbody tr:nth-child(even) {
        background: var(--color-surface-subtle);
    }

    .shortcuts-table tbody tr:hover {
        background: var(--color-brand-primary-soft);
    }

    .shortcuts-table code {
        color: var(--color-brand-primary-strong);
        background: var(--color-brand-primary-soft);
        border: 1px solid var(--color-border-accent-soft);
        border-radius: var(--radius-sm);
        padding: 0.16rem 0.44rem;
        font-size: 0.81rem;
        font-weight: 700;
    }

    .logout-modal {
        background: var(--color-surface-canvas);
        border-radius: var(--radius-xl);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
        padding: 2.5rem 2rem 2rem 2rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        min-width: 220px;
        position: relative;
    }

    .logout-modal-bar {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 8px;
        border-top-left-radius: var(--radius-xl);
        border-top-right-radius: var(--radius-xl);
        border-top: 4px solid var(--color-brand-primary);
    }

    .logout-logo {
        width: 180px;
        height: auto;
        margin-top: 12px;
        margin-bottom: 18px;
        display: block;
    }

    .logout-text {
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--color-brand-primary);
        margin-top: 0.5rem;
        letter-spacing: 0.02em;
    }

    @media (max-width: 992px) {
        .top-navbar {
            align-items: stretch;
        }

        .top-navbar-right {
            justify-content: space-between;
        }

        .searchInput {
            width: 100%;
            min-width: 0;
        }
    }
</style>
